import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, AlertCircle, CheckCircle2 } from 'lucide-react';
import LandingNavbar from '../components/LandingNavbar';

/**
 * /reset-password?token=<resetToken>
 *
 * Consumes a reset token from the forgot-password email and sets a new
 * password. After success, redirects to /staff-login.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('الرابط غير صالح — الرمز مفقود'); return; }
    if (!newPassword || !confirmNewPassword) { setError('يرجى تعبئة جميع الحقول'); return; }
    if (newPassword.length < 8) { setError('كلمة المرور يجب أن تكون 8 أحرف على الأقل'); return; }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError('يجب أن تحتوي على حرف كبير وصغير ورقم'); return;
    }
    if (newPassword !== confirmNewPassword) { setError('كلمتا المرور غير متطابقتين'); return; }

    setLoading(true);
    try {
      const axios = (await import('axios')).default;
      const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
      await axios.post(`${apiBase}/auth/reset-password`, { token, newPassword });
      setDone(true);
      setTimeout(() => navigate('/staff-login', { replace: true }), 2500);
    } catch (err) {
      setError(err?.response?.data?.message || 'تعذّر إعادة التعيين');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LandingNavbar />
      <div
        className="min-h-screen flex items-center justify-center px-6 pt-[120px] pb-16"
        dir="rtl"
        style={{ backgroundColor: '#FDFCF9' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-[440px] bg-white rounded-3xl border border-[#E8E5DD] shadow-[0_10px_40px_-10px_rgba(15,110,86,0.12)] p-8 md:p-10"
        >
          <div className="flex justify-center mb-5">
            <img src="/listech-logo.svg" alt="LIS Techs" className="h-9 w-auto" />
          </div>

          {done ? (
            <div className="text-center">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ backgroundColor: '#E1F5EE' }}
              >
                <CheckCircle2 className="w-6 h-6" style={{ color: '#0F6E56' }} />
              </div>
              <h2 className="text-[1.3rem] font-bold mb-2" style={{ color: '#1A1A1A' }}>
                تم بنجاح
              </h2>
              <p className="text-sm leading-[1.8]" style={{ color: '#666' }}>
                تم تغيير كلمة المرور. سنوجّهك لصفحة تسجيل الدخول الآن...
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h2 className="text-[1.4rem] font-bold mb-1" style={{ color: '#1A1A1A' }}>
                  كلمة مرور جديدة
                </h2>
                <p className="text-sm leading-[1.7]" style={{ color: '#8A8A8A' }}>
                  اختر كلمة مرور قوية للدخول لحسابك
                </p>
              </div>

              {error && (
                <div
                  className="mb-5 p-3.5 rounded-xl flex items-center gap-3 text-sm border"
                  style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA', color: '#B91C1C' }}
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#444' }}>
                    كلمة المرور الجديدة
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full h-12 px-4 pr-11 rounded-xl text-sm border outline-none"
                      style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E5E5', color: '#1A1A1A' }}
                      onFocus={(e) => { e.target.style.borderColor = '#0F6E56'; e.target.style.boxShadow = '0 0 0 3px rgba(15,110,86,0.08)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#E5E5E5'; e.target.style.boxShadow = 'none'; }}
                      placeholder="8 أحرف على الأقل"
                      dir="ltr"
                      autoFocus
                    />
                    <Lock className="absolute right-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px]" style={{ color: '#BFBFBF' }} />
                  </div>
                  <p className="text-[11.5px] mt-1.5" style={{ color: '#8A8A8A' }}>حرف كبير + حرف صغير + رقم</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: '#444' }}>
                    تأكيد كلمة المرور
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="w-full h-12 px-4 pr-11 rounded-xl text-sm border outline-none"
                      style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E5E5', color: '#1A1A1A' }}
                      onFocus={(e) => { e.target.style.borderColor = '#0F6E56'; e.target.style.boxShadow = '0 0 0 3px rgba(15,110,86,0.08)'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#E5E5E5'; e.target.style.boxShadow = 'none'; }}
                      placeholder="••••••••"
                      dir="ltr"
                    />
                    <Lock className="absolute right-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px]" style={{ color: '#BFBFBF' }} />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl text-sm font-bold text-white"
                  style={{
                    background: 'linear-gradient(135deg, #0F6E56 0%, #1D9E75 100%)',
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  {loading ? 'جارٍ الحفظ...' : 'حفظ كلمة المرور'}
                </button>

                <Link
                  to="/staff-login"
                  className="block text-center text-sm font-medium mt-2"
                  style={{ color: '#8A8A8A' }}
                >
                  العودة لتسجيل الدخول
                </Link>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </>
  );
}
